import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { InnerBlock, BulletList, GridCards, Callout } from "@/components/InnerBlock";
import { InnerCTA } from "@/components/InnerCTA";

const REQUIREMENTS = [
  "Explicit, recorded consent",
  "Clear privacy notices",
  "A response to any individual's request within forty-five days, at no charge",
  "A Data Protection Officer with published contact details",
  "Controls on transfers abroad, including Ministry approval for sensitive personal data processed outside Oman",
  "Breach notification to the Ministry within seventy-two hours where the breach risks people's rights",
];

const FAILURES = [
  { title: "Consent is assumed, not recorded.", desc: "There is a tick box, but no record of what was agreed, when, or to what version of the notice." },
  { title: "There is no way to answer a request.", desc: "Someone asks for a copy of their data and it takes three departments and a fortnight — well past the forty-five-day limit." },
  { title: "Deletion does not delete.", desc: "Records are flagged inactive and sit in the database forever, including in backups nobody has thought about." },
  { title: "Nobody knows where the data went.", desc: "An analytics tool, a helpdesk, an email platform — each quietly moving personal data somewhere, with no assessment behind it." },
];

const WE_BUILD_IN = [
  "Consent capture with a full record of what was agreed and when",
  "Privacy notice management in English and Arabic with versioning",
  "A data subject request workflow that can close inside forty-five days",
  "Records of processing activities maintained as the system runs",
  "Breach detection, classification and a notification runbook built for the seventy-two-hour clock",
  "Role-based access with an immutable audit trail",
  "Retention rules that actually delete, including from backups",
];

export default function PDPLPage() {
  return (
    <div data-testid="pdpl-page">
      <Seo title="Oman PDPL Compliance for Software Systems" description="What Oman's Personal Data Protection Law requires, and how we design software that meets it from the first sprint." />
      <PageHero
        eyebrow="PDPL & Compliance"
        h1="Oman's data protection law is enforced. Is your software ready?"
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Company", to: "/company" }, { label: "PDPL & compliance" }]}
      />

      <InnerBlock tint="white" heading="The law, in plain English">
        <div className="max-w-3xl space-y-4 text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          <p>Oman's Personal Data Protection Law was issued under Royal Decree 6/2022. Its Executive Regulations followed under Ministerial Decision 34/2024, and after a transition period the law has been fully enforceable since 5 February 2026. The Ministry of Transport, Communications and Information Technology supervises it.</p>
        </div>
        <div className="mt-8">
          <BulletList items={REQUIREMENTS} />
        </div>
        <p className="mt-8 text-sm italic text-wafa-ink/75">Penalties under the law run to substantial sums, and failure to report a breach carries its own separate fine.</p>
      </InnerBlock>

      <InnerBlock tint="cream" heading="Where most systems fail">
        <GridCards items={FAILURES} columns={2} />
      </InnerBlock>

      <InnerBlock tint="white" heading="What we build in">
        <BulletList items={WE_BUILD_IN} />
      </InnerBlock>

      <InnerBlock tint="cream" heading="Where your data sits is a decision, not an accident">
        <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          Under the law, transferring personal data abroad brings obligations: consent, an assessment of protection in the receiving country, and Ministry approval for sensitive personal data. Most companies never make that decision consciously — they inherit it from whatever the last developer chose.
        </p>
        <div className="mt-6">
          <Callout>We map it with you before we build: what data you hold, which of it is sensitive, where each system component would sit, and what each option means for your obligations. Then you decide, knowingly.</Callout>
        </div>
      </InnerBlock>

      <InnerBlock tint="white" heading="Going further with Meyar">
        <p className="max-w-3xl text-base leading-relaxed text-wafa-ink/85 lg:text-lg">
          Designing a compliant system is the first half. Governing it afterwards is the second. Meyar holds your risk register, controls, policies, audits, evidence and breach records, with an AI assistant on every risk measure.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/meyar" className="inline-flex items-center gap-2 rounded-md bg-wafa-primary px-5 py-3 text-sm font-semibold text-white hover:bg-wafa-deep">
            Explore Meyar <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/tools/pdpl-readiness-check" className="inline-flex items-center gap-2 rounded-md border border-wafa-deep/20 px-5 py-3 text-sm font-semibold text-wafa-deep hover:bg-wafa-cream/50">
            Check your PDPL readiness
          </Link>
        </div>
      </InnerBlock>

      <InnerCTA heading="Design compliance in from the first sprint." body="Book a call and we'll walk you through what that looks like for your system." />
    </div>
  );
}
